import { Injectable } from '@angular/core';

export interface PricingTier {
  id: string;
  name: string;
  type: 'one-time' | 'monthly' | 'yearly';
  words: number;
  price: number;
  originalPrice?: number; // For discounted yearly plans
  savings?: number; // Percentage saved
  popular?: boolean;
  description?: string;
}

export interface PricingCalculation {
  apiCostPerWord: number;
  profitMargin: number;
  totalCostPerWord: number;
}

@Injectable({
  providedIn: 'root',
})
export class PricingService {
  // API Cost Configuration (based on OpenAI pricing)
  private readonly API_COSTS = {
    // ⚡ Adaptive translation model costs:
    // - Short phrases (<10 words): gpt-4o (faster, cheaper)
    // - Longer texts (≥10 words): gpt-5 (higher quality)

    // GPT-4o for short translations (<10 words)
    GPT_4O_INPUT_TOKENS_PER_1000: 0.005, // $0.005 per 1000 input tokens (gpt-4o)
    GPT_4O_OUTPUT_TOKENS_PER_1000: 0.015, // $0.015 per 1000 output tokens (gpt-4o)

    // GPT-5 for long translations (≥10 words)
    GPT_INPUT_TOKENS_PER_1000: 0.015, // $0.015 per 1000 input tokens (gpt-5)
    GPT_OUTPUT_TOKENS_PER_1000: 0.06, // $0.06 per 1000 output tokens (gpt-5)

    // GPT-4o for OCR/Image processing
    GPT_VISION_INPUT_TOKENS_PER_1000: 0.0025, // $0.0025 per 1000 tokens for images (GPT-4o)
    GPT_VISION_OUTPUT_TOKENS_PER_1000: 0.01, // $0.010 per 1000 output tokens (GPT-4o)

    // gpt-4o-transcribe (STT)
    TRANSCRIBE_PER_MINUTE: 0.006, // $0.006 per minute

    // gpt-4o-mini-tts (TTS)
    TTS_PER_1000_CHARACTERS: 0.015, // $0.015 per 1000 characters
  };

  // Average tokens per word (English text)
  private readonly TOKENS_PER_WORD = 1.3;

  // Average characters per word
  private readonly CHARS_PER_WORD = 5;

  // System prompt overhead (estimated tokens)
  private readonly SYSTEM_PROMPT_TOKENS = 50;

  // Profit margin (150% markup = 60% profit margin)
  // This is standard for SaaS translation services
  private readonly PROFIT_MARGIN = 0.6;

  constructor() {}

  /**
   * Calculate OCR cost per image (assuming average 200 words extracted)
   */
  calculateOCRCostPerImage(): number {
    // Average OCR request:
    // 1. Image processing (GPT-4o vision)
    // 2. Text extraction and translation

    const averageWordsPerImage = 200; // Average words extracted from image
    const imageTokensPerWord = 0.5; // Estimated tokens for image processing per word
    const systemTokensPerImage = 50; // Fixed system prompt overhead

    const totalImageTokens =
      averageWordsPerImage * imageTokensPerWord + systemTokensPerImage;
    const outputTokensPerImage = averageWordsPerImage * this.TOKENS_PER_WORD;

    const imageCostPerImage =
      (totalImageTokens / 1000) *
      this.API_COSTS.GPT_VISION_INPUT_TOKENS_PER_1000;
    const outputCostPerImage =
      (outputTokensPerImage / 1000) *
      this.API_COSTS.GPT_VISION_OUTPUT_TOKENS_PER_1000;

    return imageCostPerImage + outputCostPerImage;
  }

  /**
   * Calculate the cost per word based on API usage
   * ⚡ PERFORMANCE: Uses adaptive model selection (gpt-4o for <10 words, gpt-5 for ≥10)
   */
  calculateCostPerWord(): PricingCalculation {
    // Average translation requires:
    // 1. Input text (user input)
    // 2. System prompt (fixed overhead)
    // 3. Output text (translation)

    const inputTokensPerWord = this.TOKENS_PER_WORD;
    const systemTokensPerWord = this.SYSTEM_PROMPT_TOKENS / 100; // Distributed across 100 words
    const outputTokensPerWord = this.TOKENS_PER_WORD * 1.1; // Translation might be slightly longer

    // Weighted average: 30% short (<10 words use gpt-4o), 70% long (≥10 words use gpt-5)
    const shortTextWeight = 0.3;
    const longTextWeight = 0.7;

    // Calculate API cost per word (weighted average)
    const inputCostPerWord =
      shortTextWeight *
        (inputTokensPerWord / 1000) *
        this.API_COSTS.GPT_4O_INPUT_TOKENS_PER_1000 +
      longTextWeight *
        (inputTokensPerWord / 1000) *
        this.API_COSTS.GPT_INPUT_TOKENS_PER_1000;
    const outputCostPerWord =
      shortTextWeight *
        (outputTokensPerWord / 1000) *
        this.API_COSTS.GPT_4O_OUTPUT_TOKENS_PER_1000 +
      longTextWeight *
        (outputTokensPerWord / 1000) *
        this.API_COSTS.GPT_OUTPUT_TOKENS_PER_1000;
    const systemCostPerWord =
      shortTextWeight *
        (systemTokensPerWord / 1000) *
        this.API_COSTS.GPT_4O_INPUT_TOKENS_PER_1000 +
      longTextWeight *
        (systemTokensPerWord / 1000) *
        this.API_COSTS.GPT_INPUT_TOKENS_PER_1000;

    const apiCostPerWord =
      inputCostPerWord + outputCostPerWord + systemCostPerWord;

    // Apply profit margin
    const totalCostPerWord = apiCostPerWord / (1 - this.PROFIT_MARGIN);

    return {
      apiCostPerWord,
      profitMargin: this.PROFIT_MARGIN,
      totalCostPerWord,
    };
  }

  /**
   * Get all available pricing tiers
   */
  getPricingTiers(): PricingTier[] {
    const calculation = this.calculateCostPerWord();
    const costPerWord = calculation.totalCostPerWord;

    // Minimum price per package to meet Stripe's $0.50 minimum
    const MINIMUM_PACKAGE_PRICE = 0.5;

    // One-time purchase tiers with fixed pricing
    const oneTimeTiers: PricingTier[] = [
      {
        id: '2000',
        name: '2,000 Words',
        type: 'one-time',
        words: 2000,
        price: 0.99,
        description: '2,000 word credits',
        popular: true,
      },
      {
        id: '10000',
        name: '10,000 Words',
        type: 'one-time',
        words: 10000,
        price: 3.99,
        description: '10,000 word credits',
      },
      {
        id: '20000',
        name: '20,000 Words',
        type: 'one-time',
        words: 20000,
        price: 6.99,
        description: '20,000 word credits',
      },
      {
        id: '50000',
        name: '50,000 Words',
        type: 'one-time',
        words: 50000,
        price: 13.99,
        description: '50,000 word credits',
      },
      {
        id: '150000',
        name: '150,000 Words',
        type: 'one-time',
        words: 150000,
        price: 24.99,
        description: '150,000 word credits',
      },
      {
        id: '300000',
        name: '300,000 Words',
        type: 'one-time',
        words: 300000,
        price: 44.99,
        description: '300,000 word credits',
      },
    ];

    return oneTimeTiers;
  }

  /**
   * Get pricing tiers by type
   */
  getPricingTiersByType(
    type: 'one-time' | 'monthly' | 'yearly'
  ): PricingTier[] {
    return this.getPricingTiers().filter((tier) => tier.type === type);
  }

  /**
   * Get a specific pricing tier by ID
   */
  getPricingTierById(id: string): PricingTier | undefined {
    return this.getPricingTiers().find((tier) => tier.id === id);
  }

  /**
   * Format price for display
   */
  formatPrice(price: number): string {
    return `$${price.toFixed(2)}`;
  }

  /**
   * Get pricing calculation details for admin/debugging
   */
  getPricingDetails(): any {
    const calculation = this.calculateCostPerWord();
    return {
      ...calculation,
      apiCosts: this.API_COSTS,
      tokensPerWord: this.TOKENS_PER_WORD,
      charsPerWord: this.CHARS_PER_WORD,
      systemPromptTokens: this.SYSTEM_PROMPT_TOKENS,
      profitMargin: this.PROFIT_MARGIN,
    };
  }
}
